import { useState, useEffect, useRef, useCallback } from 'react'
import { mediaAPI } from '../../services/api'
import { logger } from '@/utils/logger'

interface MediaFile {
  filename: string
  url: string
  size: number
  modified_at: number
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isImage(filename: string): boolean {
  return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(filename)
}

export default function FileManager() {
  const [files, setFiles] = useState<MediaFile[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadFiles()
  }, [])

  const loadFiles = async () => {
    try {
      const response = await mediaAPI.list()
      setFiles(response.data)
    } catch (err) {
      setError('Fehler beim Laden der Mediendateien')
      logger.error('Error loading media files:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return
    setUploading(true)
    setError('')
    setSuccess('')

    const uploaded: string[] = []
    const errors: string[] = []

    for (const file of Array.from(fileList)) {
      try {
        await mediaAPI.upload(file)
        uploaded.push(file.name)
      } catch (err: any) {
        const detail = err.response?.data?.detail
        const errorMsg =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail) && detail.length > 0
              ? (detail[0].msg ?? JSON.stringify(detail[0]))
              : err.message ?? 'Upload fehlgeschlagen'
        errors.push(`${file.name}: ${errorMsg}`)
        logger.error('Upload error:', err)
      }
    }

    await loadFiles()
    setUploading(false)

    if (errors.length > 0) {
      setError(errors.join('\n'))
    } else {
      setSuccess(`${uploaded.length} Datei${uploaded.length !== 1 ? 'en' : ''} hochgeladen`)
      setTimeout(() => setSuccess(''), 3000)
    }
  }

  const handleDelete = async (file: MediaFile) => {
    if (!confirm(`Datei "${file.filename}" wirklich löschen?`)) return
    try {
      await mediaAPI.delete(file.filename)
      setFiles(prev => prev.filter(f => f.filename !== file.filename))
      setSuccess('Datei gelöscht')
      setTimeout(() => setSuccess(''), 2000)
    } catch (err: any) {
      setError('Fehler beim Löschen der Datei')
      logger.error('Delete error:', err)
    }
  }

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(window.location.origin + url)
    setCopiedUrl(url)
    setTimeout(() => setCopiedUrl(null), 2000)
  }

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleUpload(e.dataTransfer.files)
  }, [])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      {error && (
        <div className="wc-alert wc-alert-error" style={{ marginBottom: 16, whiteSpace: 'pre-line' }}>
          {error}
        </div>
      )}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      {/* Upload Zone */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className="wc-card"
        style={{
          marginBottom: 24,
          border: `2px dashed ${isDragging ? 'var(--wc-accent)' : 'var(--wc-divider)'}`,
          background: isDragging ? 'color-mix(in srgb, var(--wc-accent) 8%, transparent)' : undefined,
          cursor: 'pointer',
          textAlign: 'center',
          padding: '40px 20px',
          alignItems: 'center',
          transition: 'border-color 0.15s ease, background-color 0.15s ease',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleUpload(e.target.files)}
        />
        {uploading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div className="wc-spinner" />
            <span className="wc-muted">Wird hochgeladen...</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="wc-muted" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <p style={{ margin: 0, fontSize: 14 }}>Dateien hier ablegen oder klicken zum Auswählen</p>
            <p className="wc-muted" style={{ margin: 0, fontSize: 12 }}>JPG, PNG, GIF, WebP, SVG — max. 10 MB pro Datei</p>
          </div>
        )}
      </div>

      {/* File Grid */}
      {files.length === 0 ? (
        <div className="wc-card wc-elev-sm" style={{ textAlign: 'center', alignItems: 'center', padding: '48px 24px' }}>
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="wc-muted" style={{ marginBottom: 8 }} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p style={{ margin: 0, fontSize: 14 }}>Keine Dateien vorhanden</p>
          <p className="wc-muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Laden Sie Bilder über den Upload-Bereich hoch</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16 }}>
          {files.map((file) => (
            <div
              key={file.filename}
              className="wc-card wc-elev-sm"
              style={{ padding: 0, overflow: 'hidden', gap: 0 }}
            >
              {/* Thumbnail */}
              <div
                style={{
                  aspectRatio: '1 / 1',
                  background: 'var(--wc-bg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {isImage(file.filename) ? (
                  <img
                    src={file.url}
                    alt={file.filename}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    loading="lazy"
                  />
                ) : (
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="wc-muted" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}

                {/* Hover Overlay */}
                <div
                  className="wc-file-overlay"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(10, 11, 18, 0.55)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    opacity: 0,
                    transition: 'opacity 0.15s ease',
                  }}
                >
                  <button
                    onClick={() => handleCopyUrl(file.url)}
                    title="URL kopieren"
                    className="wc-btn wc-btn-secondary wc-btn-icon"
                    style={{ background: 'var(--wc-surface)' }}
                  >
                    {copiedUrl === file.url ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6ee7b7" strokeWidth="2" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(file)}
                    title="Löschen"
                    className="wc-btn wc-btn-secondary wc-btn-icon"
                    style={{ background: 'var(--wc-surface)', color: '#fca5a5' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* File Info */}
              <div style={{ padding: 8, borderTop: '1px solid var(--wc-divider)' }}>
                <p style={{ margin: 0, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.filename}>
                  {file.filename}
                </p>
                <p className="wc-muted" style={{ margin: 0, fontSize: 11 }}>{formatSize(file.size)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <p className="wc-muted" style={{ fontSize: 13, marginTop: 16 }}>
          {files.length} Datei{files.length !== 1 ? 'en' : ''} — Hover über ein Bild um URL zu kopieren oder zu löschen
        </p>
      )}

      <style>{`
        .wc-card:hover > div > .wc-file-overlay { opacity: 1; }
      `}</style>
    </div>
  )
}
